import interceptor from '@/common/interceptor/interceptor';

/** Vital measures the telemetry API returns and the Sensor modal charts; mirrors `sensors.telemetry.measures.VITALS`. */
export type TelemetryVital =
  | 'temperature'
  | 'humidity'
  | 'pressure'
  | 'light'
  | 'tilt'
  | 'battery'
  | 'shock_value'
  | 'cell_signal';

/** Measure names the telemetry API returns; mirrors `sensors.telemetry.measures.MEASURES`. */
export type TelemetryMeasure =
  TelemetryVital | 'latitude' | 'longitude' | 'gps_accuracy_m';

/** A measure's last reading before delivery; mirrors `AtDeliverySerializer` (epoch ms). */
export interface ReadingAtDelivery {
  at: number;
  value: number | null;
}

/** One measure's extent for one sensor; mirrors `MeasureSummarySerializer` (epoch ms). */
export interface MeasureSummary {
  first_at: number;
  last_at: number;
  count: number;
  latest: number | null;
  at_delivery: ReadingAtDelivery | null;
}

/** One attached sensor with data; mirrors `SensorSummarySerializer`. */
export interface SensorSummary {
  id: string;
  label: string | null;
  tracking_id: string;
  vendor: string | null;
  priority: number;
  measures: Partial<Record<TelemetryMeasure, MeasureSummary>>;
}

/** Payload of `GET /api/shipments/<id>/telemetry/summary/`; mirrors `TelemetrySummarySerializer`. */
export interface TelemetrySummary {
  shipment_id: string;
  anchors: {
    picked_up: number | null;
    delivered: number | null;
    first_reading: number | null;
    last_reading: number | null;
  };
  sensors: SensorSummary[];
}

interface Envelope<T> {
  data: { data: T };
}

/** Fetches a shipment's telemetry summary, using the share token instead of the login when given; failures reject without a toast. */
export const fetchTelemetrySummary = async (
  shipmentId: string,
  shareToken?: string
): Promise<TelemetrySummary> => {
  const path = `/shipments/${shipmentId}/telemetry/summary/`;
  const response = shareToken
    ? await interceptor.axiosWithoutHaeders(
        'Get',
        `${path}?token=${encodeURIComponent(shareToken)}`,
        undefined,
        null,
        true
      )
    : await interceptor.axiosGet('Get', path, undefined, undefined, null, true);
  // The interceptor resolves only 2xx responses, whose body is always CustomResponse's envelope.
  return (response.data as Envelope<TelemetrySummary>).data.data;
};

/** One measure's readings, parallel arrays of epoch-ms times and values; mirrors `MeasureSeriesSerializer`. */
export interface MeasureSeries {
  t: number[];
  v: number[];
}

/** Readings keyed by sensor id, then measure. */
export type SeriesBySensor = Record<
  string,
  Partial<Record<TelemetryMeasure, MeasureSeries>>
>;

/** Payload of `GET /api/shipments/<id>/telemetry/series/`; mirrors `TelemetrySeriesSerializer`. */
export interface TelemetrySeries {
  shipment_id: string;
  window: { from: number; to: number };
  series: SeriesBySensor;
}

/** Which readings to fetch; `from`/`to` are epoch ms and default to the operational window. */
export interface SeriesRequest {
  measures: readonly TelemetryMeasure[];
  from?: number;
  to?: number;
  includeEnd?: boolean;
}

/** Fetches raw readings for a window, using the share token instead of the login when given; failures reject without a toast. */
export const fetchTelemetrySeries = async (
  shipmentId: string,
  request: SeriesRequest,
  shareToken?: string,
  signal?: AbortSignal
): Promise<TelemetrySeries> => {
  const params = new URLSearchParams({ measures: request.measures.join(',') });
  if (request.from !== undefined) params.set('from', String(request.from));
  if (request.to !== undefined) params.set('to', String(request.to));
  if (request.includeEnd) params.set('include_end', 'true');
  if (shareToken) params.set('token', shareToken);
  const path = `/shipments/${shipmentId}/telemetry/series/?${params.toString()}`;
  const response = shareToken
    ? await interceptor.axiosWithoutHaeders('Get', path, undefined, null, true)
    : await interceptor.axiosGet(
        'Get',
        path,
        undefined,
        undefined,
        signal ?? null,
        true
      );
  // The interceptor resolves only 2xx responses, whose body is always CustomResponse's envelope.
  return (response.data as Envelope<TelemetrySeries>).data.data;
};
